import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form, Button, Modal, Spinner, Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import './AutoTradingRules.css';
import { AUTO_TRADING_CONFIG } from '../../../shared/services/config';
import { formatToLocalTime } from '../../../shared/utils/dateUtils';

const CONDITION_OPTIONS = [
  { value: 'PRICE_BELOW', label: 'Price Below', threshold: true, window: false },
  { value: 'PRICE_ABOVE', label: 'Price Above', threshold: true, window: false },
  { value: 'PCT_CHANGE_ABOVE', label: '% Change Above', threshold: true, window: true },
  { value: 'PCT_CHANGE_BELOW', label: '% Change Below', threshold: true, window: true },
  { value: 'RSI_BELOW', label: 'RSI Below', threshold: true, window: false, params: ['period'] },
  { value: 'RSI_ABOVE', label: 'RSI Above', threshold: true, window: false, params: ['period'] },
  { value: 'SMA_CROSS_UP', label: 'SMA Cross Up', threshold: false, window: false, params: ['fast', 'slow'] },
  { value: 'SMA_CROSS_DOWN', label: 'SMA Cross Down', threshold: false, window: false, params: ['fast', 'slow'] },
  { value: 'MACD_CROSS_UP', label: 'MACD Cross Up', threshold: false, window: false },
  { value: 'MACD_CROSS_DOWN', label: 'MACD Cross Down', threshold: false, window: false },
];

const WINDOW_OPTIONS = [
  { value: '5min', label: '5 min' },
  { value: '15min', label: '15 min' },
  { value: '30min', label: '30 min' },
  { value: '1hour', label: '1 hour' },
  { value: '4hour', label: '4 hour' },
];

const DEFAULT_CONDITION = {
  condition_type: 'PRICE_ABOVE',
  threshold_value: '',
  time_window: '5min',
  params: {},
};

const parseSymbolDetailsMap = (raw) => {
  if (!raw) return {};
  let parsed = raw;
  try {
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed;
  } catch (_) {
    return {};
  }
};

const AutoTradingRules = ({ profileId, token }) => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const [rules, setRules] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [autoTrades, setAutoTrades] = useState([]);
  const [watchlists, setWatchlists] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [watchlistNameMap, setWatchlistNameMap] = useState({});
  const [symbolNameMap, setSymbolNameMap] = useState({});
  const [watchlistSymbolMap, setWatchlistSymbolMap] = useState({});
  const [pauseState, setPauseState] = useState({ all: false, rules: [], symbols: [] });
  const [loading, setLoading] = useState(false);
  const [loadingExec, setLoadingExec] = useState(false);
  const [loadingPerf, setLoadingPerf] = useState(false);
  const [perfRange, setPerfRange] = useState('all');
  const [performance, setPerformance] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingGap, setLoadingGap] = useState(false);
  const [savingGap, setSavingGap] = useState(false);
  const [ruleGapMinutes, setRuleGapMinutes] = useState(5);
  const [marketHoursMode, setMarketHoursMode] = useState('AUTO');
  const [marketHoursDecision, setMarketHoursDecision] = useState(null);
  const [loadingMarketHours, setLoadingMarketHours] = useState(false);
  const [savingMarketHours, setSavingMarketHours] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [expandedRuleId, setExpandedRuleId] = useState(null);
  const [miniTooltip, setMiniTooltip] = useState(null);

  const normalizePerformancePayload = (payload) => {
    if (!payload || typeof payload !== 'object') return null;
    const byRuleRaw = payload.by_rule;
    let byRule = [];
    if (Array.isArray(byRuleRaw)) {
      byRule = byRuleRaw;
    } else if (byRuleRaw && typeof byRuleRaw === 'object') {
      byRule = Object.entries(byRuleRaw).map(([ruleName, stat]) => ({
        rule_name: ruleName,
        trades: stat?.trades ?? stat?.count ?? 0,
        total_pnl: stat?.total_pnl ?? stat?.pnl ?? 0,
        win_rate: stat?.win_rate ?? null,
        avg_return_pct: stat?.avg_return_pct ?? null,
      }));
    }
    const overall = payload.overall || {
      trades: payload.trades ?? payload.executions ?? 0,
      win_rate: payload.win_rate ?? null,
      total_pnl: payload.total_pnl ?? null,
      avg_return_pct: payload.avg_return_pct ?? null,
    };
    return {
      ...payload,
      overall,
      by_rule: byRule,
      by_rule_symbol: Array.isArray(payload.by_rule_symbol) ? payload.by_rule_symbol : [],
      by_rule_watchlist: Array.isArray(payload.by_rule_watchlist) ? payload.by_rule_watchlist : [],
    };
  };

  const normalizeExecutionShape = (exec) => ({
    ...exec,
    rule_set_id: exec.rule_set_id ?? exec.ruleSetId ?? null,
    ticker_symbol: exec.ticker_symbol ?? exec.tickerSymbol ?? null,
    created_at: exec.created_at ?? exec.createdAt ?? null,
    profit_loss: exec.profit_loss ?? exec.profitLoss ?? null,
    return_pct: exec.return_pct ?? exec.returnPct ?? null,
  });

  const normalizeTradeShape = (trade) => ({
    ...trade,
    created_at: trade.created_at ?? trade.createdAt ?? null,
    closed_at: trade.closed_at ?? trade.closedAt ?? null,
    ticker_symbol: trade.ticker_symbol ?? trade.tickerSymbol ?? null,
    trade_type: trade.trade_type ?? trade.tradeType ?? null,
    entry_price: trade.entry_price ?? trade.entryPrice ?? null,
    exit_price: trade.exit_price ?? trade.exitPrice ?? null,
    order_type: trade.order_type ?? trade.orderType ?? null,
    close_reason: trade.close_reason ?? trade.closeReason ?? null,
    profit_loss: trade.profit_loss ?? trade.profitLoss ?? null,
    profit_loss_percent: trade.profit_loss_percent ?? trade.profitLossPercent ?? null,
    rule_set_id: trade.rule_set_id ?? trade.ruleSetId ?? null,
  });

  const toNumber = (value) => {
    if (value == null) return null;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[$,%]/g, '').replace(/,/g, '').trim();
      if (!cleaned) return null;
      const num = Number(cleaned);
      return Number.isFinite(num) ? num : null;
    }
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const formatPercent = (value, decimals = 1) => {
    const num = toNumber(value);
    if (num == null) return '—';
    const pct = num >= 0 && num <= 1 ? num * 100 : num;
    return `${pct.toFixed(decimals)}%`;
  };

  const formatCurrency = (value, decimals = 2, showSign = true) => {
    const num = toNumber(value);
    if (num == null) return '—';
    const sign = showSign ? (num > 0 ? '+' : num < 0 ? '-' : '') : '';
    return `${sign}$${Math.abs(num).toFixed(decimals)}`;
  };

  const toneFromValue = (value) => {
    const num = toNumber(value);
    if (num == null) return 'neutral';
    if (num > 0) return 'positive';
    if (num < 0) return 'negative';
    return 'neutral';
  };

  const toneFromWinRate = (value) => {
    const num = toNumber(value);
    if (num == null) return 'neutral';
    if (num >= 80) return 'best';
    if (num >= 50) return 'ok';
    if (num >= 30) return 'not-ok';
    return 'danger';
  };

  const toDayKey = (value) => {
    if (!value) return null;
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return null;
    const year = dt.getFullYear();
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const detailTypeFromText = (text) => {
    const normalized = String(text || '').trim().toUpperCase();
    if (!normalized) return null;
    if (normalized.includes('TAKE_PROFIT') || normalized.includes('TAKE PROFIT')) return 'TAKE_PROFIT';
    if (normalized.includes('STOP_LOSS') || normalized.includes('STOP LOSS')) return 'STOP_LOSS';
    if (normalized.includes('MANUAL')) return 'MANUAL';
    if (normalized.includes('TRAILING')) return 'TRAILING_STOP';
    if (normalized.includes('TIMEOUT') || normalized.includes('TIME EXIT')) return 'TIME_EXIT';
    if (normalized.includes('SIGNAL')) return 'STRATEGY_SIGNAL';
    return normalized.length > 30 ? `${normalized.slice(0, 30)}…` : normalized;
  };

  const perfRecords = useMemo(() => {
    const mappedPerfTrades = Array.isArray(performance?.trades)
      ? performance.trades.map(trade => {
        const entryPrice = toNumber(trade.entry_price);
        const exitPrice = toNumber(trade.exit_price);
        const computedPnl =
          entryPrice != null && exitPrice != null && trade.quantity != null
            ? (exitPrice - entryPrice) * Number(trade.quantity)
            : null;
        const profitLoss = toNumber(trade.profit_loss);
        return {
          id: trade.id,
          created_at: trade.closed_at || trade.created_at,
          ticker_symbol: trade.ticker_symbol,
          action: trade.trade_type,
          quantity: trade.quantity,
          entry_price: trade.entry_price,
          exit_price: trade.exit_price,
          price: trade.exit_price ?? trade.entry_price,
          profit_loss: profitLoss ?? computedPnl,
          return_pct: null,
          status: trade.status,
          rule_set_id: trade.rule_set_id ?? null,
          source: 'trade',
        };
      })
      : [];

    const mappedAutoTrades = autoTrades.map(trade => {
      const entryPrice = toNumber(trade.entry_price);
      const exitPrice = toNumber(trade.exit_price);
      const computedPnl =
        entryPrice != null && exitPrice != null && trade.quantity != null
          ? (exitPrice - entryPrice) * Number(trade.quantity)
          : null;
      const profitLoss = toNumber(trade.profit_loss);
      return {
        id: trade.id,
        created_at: trade.created_at,
        closed_at: trade.closed_at,
        ticker_symbol: trade.ticker_symbol,
        action: trade.trade_type,
        quantity: trade.quantity,
        entry_price: trade.entry_price,
        exit_price: trade.exit_price,
        price: trade.exit_price ?? trade.entry_price,
        profit_loss: profitLoss ?? computedPnl,
        return_pct: trade.profit_loss_percent ?? trade.return_pct,
        status: trade.status,
        rule_set_id: trade.rule_set_id ?? null,
        source: 'paper',
      };
    });

    const mappedExecutions = executions.map(exec => ({
      id: exec.id,
      created_at: exec.created_at,
      ticker_symbol: exec.ticker_symbol,
      action: exec.action,
      quantity: exec.quantity,
      entry_price: null,
      exit_price: exec.price,
      price: exec.price,
      profit_loss: exec.profit_loss,
      return_pct: exec.return_pct,
      status: exec.status,
      rule_set_id: exec.rule_set_id,
      source: 'execution',
    }));

    const inferRuleSetId = (records) => {
      const execBySymbol = mappedExecutions.reduce((acc, exec) => {
        if (!exec.ticker_symbol || exec.rule_set_id == null) return acc;
        if (!acc[exec.ticker_symbol]) acc[exec.ticker_symbol] = [];
        acc[exec.ticker_symbol].push(exec);
        return acc;
      }, {});
      Object.values(execBySymbol).forEach(list =>
        list.sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime())
      );

      return records.map((record) => {
        if (record.rule_set_id != null || !record.ticker_symbol) return record;
        const candidates = execBySymbol[record.ticker_symbol] || [];
        if (!candidates.length) return record;

        const recordTs = new Date(record.closed_at || record.created_at || 0).getTime();
        if (!Number.isFinite(recordTs) || recordTs <= 0) {
          return { ...record, rule_set_id: candidates[candidates.length - 1].rule_set_id };
        }

        let best = candidates[candidates.length - 1];
        let bestDelta = Number.POSITIVE_INFINITY;
        for (const candidate of candidates) {
          const execTs = new Date(candidate.created_at || 0).getTime();
          if (!Number.isFinite(execTs)) continue;
          const delta = Math.abs(recordTs - execTs);
          if (delta < bestDelta) {
            best = candidate;
            bestDelta = delta;
          }
        }
        return { ...record, rule_set_id: best?.rule_set_id ?? null };
      });
    };

    const mappedPerfTradesWithRule = inferRuleSetId(mappedPerfTrades);
    const perfTradesHaveRealizedPnl = mappedPerfTradesWithRule.some((trade) => trade.profit_loss != null);
    if (mappedPerfTradesWithRule.length && perfTradesHaveRealizedPnl) {
      return mappedPerfTradesWithRule;
    }

    const autoTradesHaveRuleMapping = mappedAutoTrades.some((trade) => trade.rule_set_id != null);
    if (autoTradesHaveRuleMapping) {
      return mappedAutoTrades;
    }

    const enrichedAutoTrades = inferRuleSetId(mappedAutoTrades);

    const enrichedHasRuleMapping = enrichedAutoTrades.some((trade) => trade.rule_set_id != null);
    if (enrichedHasRuleMapping) {
      return enrichedAutoTrades;
    }
    if (mappedExecutions.length) {
      return mappedExecutions;
    }
    return enrichedAutoTrades;
  }, [autoTrades, executions, performance]);

  const perfTrend = useMemo(() => {
    if (!perfRecords.length) {
      return { points: [], deltas: [], min: 0, max: 0, latest: null };
    }
    const rangeDays = perfRange === 'all' ? null : Number(perfRange);
    const cutoff = rangeDays ? Date.now() - rangeDays * 24 * 60 * 60 * 1000 : null;
    const realized = perfRecords
      .filter(exec => exec.created_at && exec.profit_loss != null)
      .filter(exec => {
        if (!cutoff) return true;
        const createdAt = new Date(exec.created_at).getTime();
        return Number.isFinite(createdAt) && createdAt >= cutoff;
      })
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

    if (!realized.length) {
      return { points: [], deltas: [], min: 0, max: 0, latest: null };
    }

    const series = [];
    const deltas = [];
    let running = 0;
    for (const exec of realized) {
      const tradePnl = toNumber(exec.profit_loss) ?? 0;
      deltas.push(tradePnl);
      running += tradePnl;
      series.push(running);
    }

    const trimmed = series.length > 40 ? series.slice(-40) : series;
    const trimmedDeltas = deltas.length > 40 ? deltas.slice(-40) : deltas;
    const min = Math.min(...trimmed);
    const max = Math.max(...trimmed);
    return {
      points: trimmed,
      deltas: trimmedDeltas,
      min,
      max,
      latest: trimmed[trimmed.length - 1],
    };
  }, [perfRecords, perfRange]);

  const perfComboTrends = useMemo(() => {
    const rangeDays = perfRange === 'all' ? null : Number(perfRange);
    const cutoff = rangeDays ? Date.now() - rangeDays * 24 * 60 * 60 * 1000 : null;

    const byRuleSymbol = {};
    const byRuleWatchlist = {};
    const watchlistEntries = Object.entries(watchlistSymbolMap || {}).map(([id, list]) => [
      Number(id),
      new Set(list || []),
    ]);

    for (const exec of perfRecords) {
      if (exec.rule_set_id == null || exec.profit_loss == null) continue;
      const createdAt = exec.created_at ? new Date(exec.created_at).getTime() : NaN;
      if (cutoff && (!Number.isFinite(createdAt) || createdAt < cutoff)) continue;
      const pnl = toNumber(exec.profit_loss);
      if (pnl == null) continue;

      const symbolKey = `${exec.rule_set_id}::${exec.ticker_symbol}`;
      byRuleSymbol[symbolKey] = byRuleSymbol[symbolKey] || [];
      byRuleSymbol[symbolKey].push({ ts: createdAt, pnl, symbol: exec.ticker_symbol });

      for (const [watchlistId, symbolSet] of watchlistEntries) {
        if (exec.ticker_symbol && symbolSet.has(exec.ticker_symbol)) {
          const watchlistKey = `${exec.rule_set_id}::${watchlistId}`;
          byRuleWatchlist[watchlistKey] = byRuleWatchlist[watchlistKey] || [];
          byRuleWatchlist[watchlistKey].push({ ts: createdAt, pnl, symbol: exec.ticker_symbol });
        }
      }
    }

    const toTrend = (items) => {
      if (!items || items.length === 0) return { points: [], min: 0, max: 0, latest: null };
      const sorted = [...items].sort((a, b) => a.ts - b.ts);
      const trimmedItems = sorted.length > 24 ? sorted.slice(-24) : sorted;
      const points = trimmedItems.map((item) => item.pnl);
      const symbols = trimmedItems.map((item) => item.symbol || '');
      const min = Math.min(0, ...points);
      const max = Math.max(0, ...points);
      const latest = points.reduce((sum, value) => sum + value, 0);
      return { points, symbols, min, max, latest };
    };

    return {
      byRuleSymbol: Object.fromEntries(
        Object.entries(byRuleSymbol).map(([key, items]) => [key, toTrend(items)])
      ),
      byRuleWatchlist: Object.fromEntries(
        Object.entries(byRuleWatchlist).map(([key, items]) => [key, toTrend(items)])
      ),
    };
  }, [perfRecords, perfRange, watchlistSymbolMap]);

  const renderMiniSparkline = (trend, options = {}) => {
    if (!trend || trend.points.length === 0) {
      return <div className="auto-trade-perf-mini-empty">No trend</div>;
    }
    const label = options.label || 'Series';
    const pnlText = formatCurrency(options.pnl);
    const pnlPctText = formatPercent(options.pnlPct, 2);
    const tooltipKey = options.tooltipKey || label;
    const showMiniTooltip = (symbol, tradePnl, tradeIndex) => {
      setMiniTooltip({
        key: tooltipKey,
        lines: [
          label,
          `Symbol: ${symbol}`,
          `Trade ${tradeIndex + 1}: ${formatCurrency(tradePnl)}`,
          `Total P&L: ${pnlText} (${pnlPctText})`,
        ],
      });
    };

    const width = 160;
    const height = 42;
    const padding = 4;
    const usableWidth = width - padding * 2;
    const usableHeight = height - padding * 2;
    const cumulativePoints = trend.points.reduce((acc, value) => {
      const running = (acc.length ? acc[acc.length - 1] : 0) + Number(value || 0);
      acc.push(running);
      return acc;
    }, []);
    const count = cumulativePoints.length;
    const min = Math.min(0, ...cumulativePoints);
    const max = Math.max(0, ...cumulativePoints);
    const span = max - min || 1;
    const zeroY = padding + usableHeight - ((0 - min) / span) * usableHeight;
    const pointX = (idx) => {
      if (count <= 1) return padding + usableWidth / 2;
      return padding + (usableWidth * idx) / (count - 1);
    };
    const pointY = (value) => padding + usableHeight - ((value - min) / span) * usableHeight;
    const barSlotWidth = usableWidth / Math.max(count, 1);
    const barWidth = Math.max(2.5, Math.min(7, barSlotWidth * 0.5));
    const linePath = cumulativePoints
      .map((value, idx) => `${idx === 0 ? 'M' : 'L'} ${pointX(idx)} ${pointY(value)}`)
      .join(' ');
    const areaPath = count
      ? `${linePath} L ${pointX(count - 1)} ${zeroY} L ${pointX(0)} ${zeroY} Z`
      : '';
    const totalPnl = Number(options.pnl || 0);
    const strokeColor = totalPnl > 0 ? '#16a34a' : totalPnl < 0 ? '#dc2626' : '#0ea5a4';
    const clipBase = `mini-${String(tooltipKey).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    const positiveClipId = `${clipBase}-pos`;
    const negativeClipId = `${clipBase}-neg`;
    const positiveClipHeight = Math.max(Math.min(zeroY, height) - 0, 0);
    const negativeClipY = Math.max(Math.min(zeroY, height), 0);
    const negativeClipHeight = Math.max(height - negativeClipY, 0);

    return (
      <div className="auto-trade-mini-chart-wrap">
        <svg
          className="auto-trade-sparkline auto-trade-sparkline-mini"
          viewBox="0 0 160 42"
          preserveAspectRatio="none"
          onMouseLeave={() => setMiniTooltip(null)}
        >
          <defs>
            <clipPath id={positiveClipId}>
              <rect x="0" y="0" width={width} height={positiveClipHeight} />
            </clipPath>
            <clipPath id={negativeClipId}>
              <rect x="0" y={negativeClipY} width={width} height={negativeClipHeight} />
            </clipPath>
          </defs>
          <line
            x1={padding}
            y1={zeroY}
            x2={width - padding}
            y2={zeroY}
            stroke="rgba(15, 23, 42, 0.24)"
            strokeWidth="1"
          />
          {trend.points.map((deltaValue, idx) => {
            const xCenter = pointX(idx);
            const deltaY = pointY(deltaValue);
            const y = deltaValue >= 0 ? deltaY : zeroY;
            const heightBar = Math.max(Math.abs(deltaY - zeroY), 1.1);
            const fill = deltaValue >= 0 ? 'rgba(22, 163, 74, 0.64)' : 'rgba(220, 38, 38, 0.64)';
            return (
              <rect
                key={`mini-delta-bar-${label}-${idx}`}
                x={xCenter - barWidth / 2}
                y={y}
                width={barWidth}
                height={heightBar}
                rx="1.2"
                fill={fill}
              />
            );
          })}
          {count > 1 && (
            <path
              d={areaPath}
              fill="rgba(22, 163, 74, 0.22)"
              clipPath={`url(#${positiveClipId})`}
              stroke="none"
            />
          )}
          {count > 1 && (
            <path
              d={areaPath}
              fill="rgba(220, 38, 38, 0.2)"
              clipPath={`url(#${negativeClipId})`}
              stroke="none"
            />
          )}
          {count > 1 && (
            <path
              d={linePath}
              fill="none"
              stroke={strokeColor}
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          {cumulativePoints.map((value, idx) => {
            const x = pointX(idx);
            const y = pointY(value);
            const dotFill = value > 0 ? '#16a34a' : value < 0 ? '#dc2626' : '#0ea5a4';
            const symbol = trend.symbols?.[idx] || label;
            return (
              <g key={`${label}-${idx}`}>
                <circle
                  cx={x}
                  cy={y}
                  r={2.7}
                  fill={dotFill}
                  stroke="#ffffff"
                  strokeWidth="0.6"
                />
                <circle
                  cx={x}
                  cy={y}
                  r={5.5}
                  fill="transparent"
                  onMouseEnter={() => showMiniTooltip(symbol, trend.points[idx], idx)}
                  onMouseMove={() => showMiniTooltip(symbol, trend.points[idx], idx)}
                />
              </g>
            );
          })}
        </svg>
        {miniTooltip?.key === tooltipKey && (
          <div className="auto-trade-mini-tooltip-inline">
            {miniTooltip.lines.map((line, idx) => (
              <div key={`mini-inline-${idx}`}>{line}</div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const unifiedExecutionRows = useMemo(() => {
    const trades = Array.isArray(autoTrades) ? autoTrades : [];
    if (!trades.length) {
      return (executions || []).map((exec) => ({
        id: exec.id,
        created_at: exec.created_at,
        ticker_symbol: exec.ticker_symbol,
        buy_time: exec.created_at,
        entry_price: null,
        buy_quantity: Number(exec.quantity || 0),
        sell_time: null,
        exit_price: null,
        sell_quantity: null,
        pnl: null,
        pnl_pct: null,
        derived_status: (exec.status || '').toUpperCase() || 'OPEN',
        details: exec.details || '',
      }));
    }

    const normalized = trades.map((trade) => ({
      id: trade.id,
      created_at: trade.created_at,
      ticker_symbol: trade.ticker_symbol,
      trade_type: (trade.trade_type || '').toUpperCase(),
      quantity: Number(trade.quantity || 0),
      entry_price: toNumber(trade.entry_price),
      exit_price: toNumber(trade.exit_price),
      profit_loss: toNumber(trade.profit_loss),
      status: (trade.status || '').toUpperCase(),
      close_reason: trade.close_reason || '',
      closed_at: trade.closed_at,
    }));

    const ascending = [...normalized].sort((a, b) => {
      const ta = new Date(a.created_at || 0).getTime();
      const tb = new Date(b.created_at || 0).getTime();
      if (ta !== tb) return ta - tb;
      return Number(a.id || 0) - Number(b.id || 0);
    });

    const openLotsBySymbol = {};
    const rowsByBuyTradeId = {};
    const lifecycleRows = [];

    ascending.forEach((row) => {
      const symbol = (row.ticker_symbol || '').toUpperCase();
      if (!symbol || row.quantity <= 0) return;

      if (row.trade_type === 'BUY') {
        const lifecycle = {
          id: row.id,
          ticker_symbol: symbol,
          buy_time: row.created_at,
          entry_price: row.entry_price,
          buy_quantity: row.quantity,
          sell_time: null,
          exit_price: null,
          sell_quantity: 0,
          pnl: 0,
          pnl_pct: null,
          details: '',
          derived_status: 'OPEN',
        };
        rowsByBuyTradeId[row.id] = lifecycle;
        lifecycleRows.push(lifecycle);
        openLotsBySymbol[symbol] = openLotsBySymbol[symbol] || [];
        openLotsBySymbol[symbol].push({
          trade_id: row.id,
          remaining: row.quantity,
          entry_price: Number(row.entry_price || 0),
        });
        return;
      }

      if (
        row.trade_type === 'SELL' &&
        row.status === 'CLOSED' &&
        row.exit_price != null &&
        Number(row.exit_price) > 0
      ) {
        let remainingToMatch = row.quantity;
        const lots = openLotsBySymbol[symbol] || [];
        const sellExitPrice = Number(row.exit_price);
        for (let i = 0; i < lots.length && remainingToMatch > 0; i += 1) {
          const lot = lots[i];
          if (lot.remaining <= 0) continue;
          const consumed = Math.min(lot.remaining, remainingToMatch);
          lot.remaining -= consumed;
          remainingToMatch -= consumed;
          const lifecycle = rowsByBuyTradeId[lot.trade_id];
          if (!lifecycle) continue;
          const prevSellQty = lifecycle.sell_quantity;
          const nextSellQty = prevSellQty + consumed;
          lifecycle.sell_time = row.closed_at || row.created_at || lifecycle.sell_time;
          lifecycle.sell_quantity = nextSellQty;
          const prevProceeds = (lifecycle.exit_price || 0) * prevSellQty;
          const nextProceeds = prevProceeds + sellExitPrice * consumed;
          lifecycle.exit_price = nextSellQty > 0 ? nextProceeds / nextSellQty : null;
          lifecycle.pnl = (lifecycle.pnl || 0) + (sellExitPrice - Number(lot.entry_price || 0)) * consumed;
          if (nextSellQty > 0 && lifecycle.entry_price != null && lifecycle.entry_price > 0) {
            const costBasis = lifecycle.entry_price * nextSellQty;
            lifecycle.pnl_pct = costBasis > 0 ? (lifecycle.pnl / costBasis) * 100 : null;
          }
          lifecycle.details = row.close_reason || lifecycle.details;
          lifecycle.derived_status = lot.remaining > 0 ? 'OPEN' : 'CLOSED';
        }
      }
    });

    return lifecycleRows
      .map((row) => ({
        ...row,
        sell_time: row.sell_quantity > 0 ? row.sell_time : null,
        exit_price: row.sell_quantity > 0 ? row.exit_price : null,
        sell_quantity: row.sell_quantity > 0 ? row.sell_quantity : null,
        pnl: row.sell_quantity > 0 ? row.pnl : null,
        pnl_pct: row.sell_quantity > 0 ? row.pnl_pct : null,
      }))
      .sort((a, b) => {
        const ta = new Date(a.buy_time || 0).getTime();
        const tb = new Date(b.buy_time || 0).getTime();
        if (tb !== ta) return tb - ta;
        return Number(b.id || 0) - Number(a.id || 0);
      });
  }, [autoTrades, executions]);

  const executionsGroupedByStatus = useMemo(() => {
    const groups = {};
    (unifiedExecutionRows || []).forEach((row) => {
      const status = (row?.derived_status || 'UNKNOWN').toUpperCase();
      if (!groups[status]) groups[status] = [];
      groups[status].push(row);
    });

    const preferredOrder = ['OPEN', 'CLOSED', 'CANCELED'];
    const orderedStatuses = [
      ...preferredOrder.filter((status) => groups[status]?.length),
      ...Object.keys(groups)
        .filter((status) => !preferredOrder.includes(status))
        .sort(),
    ];

    return orderedStatuses.map((status) => ({
      status,
      rows: groups[status] || [],
    }));
  }, [unifiedExecutionRows]);

  const executionComparison = useMemo(() => {
    const dayMap = {};
    const detailTotals = {};
    const detailLabels = {};

    const sourceRows = Array.isArray(autoTrades) ? autoTrades : [];
    if (!sourceRows.length) {
      return { rows: [], detailKeys: [] };
    }

    sourceRows.forEach((row) => {
      const action = String(row.trade_type || row.action || '').toUpperCase();
      const status = String(row.status || '').toUpperCase();
      const isClosedSellExit =
        action === 'SELL' &&
        status === 'CLOSED' &&
        Boolean(row.closed_at || row.close_reason);

      const buyDayKey = action === 'BUY' ? toDayKey(row.created_at) : null;
      const sellDayKey = isClosedSellExit ? toDayKey(row.closed_at || row.created_at) : null;

      if (buyDayKey) {
        if (!dayMap[buyDayKey]) dayMap[buyDayKey] = { date: buyDayKey, buy_count: 0, sell_count: 0 };
        dayMap[buyDayKey].buy_count += 1;
      }
      if (sellDayKey) {
        if (!dayMap[sellDayKey]) dayMap[sellDayKey] = { date: sellDayKey, buy_count: 0, sell_count: 0 };
        dayMap[sellDayKey].sell_count += 1;
      }

      const detailType = detailTypeFromText(row.close_reason || row.details);
      if (!detailType || !isClosedSellExit) return;
      const detailDayKey = toDayKey(row.closed_at || row.created_at);
      if (!detailDayKey) return;
      if (!dayMap[detailDayKey]) dayMap[detailDayKey] = { date: detailDayKey, buy_count: 0, sell_count: 0 };
      const detailKey = `detail_${detailType.replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '')}`;
      dayMap[detailDayKey][detailKey] = (dayMap[detailDayKey][detailKey] || 0) + 1;
      detailTotals[detailKey] = (detailTotals[detailKey] || 0) + 1;
      detailLabels[detailKey] = detailType;
    });

    const detailKeys = Object.entries(detailTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([key]) => ({ key, label: detailLabels[key] || key }));

    const rows = Object.keys(dayMap)
      .sort((a, b) => a.localeCompare(b))
      .slice(-14)
      .map((dayKey) => {
        const row = { ...dayMap[dayKey] };
        detailKeys.forEach(({ key }) => {
          row[key] = row[key] || 0;
        });
        return row;
      });

    return { rows, detailKeys };
  }, [autoTrades, executions]);

  const derivedPerformance = useMemo(() => {
    const rangeDays = perfRange === 'all' ? null : Number(perfRange);
    const cutoff = rangeDays ? Date.now() - rangeDays * 24 * 60 * 60 * 1000 : null;

    const filtered = perfRecords.filter(exec => {
      if (!exec.created_at) return !cutoff;
      if (!cutoff) return true;
      const createdAt = new Date(exec.created_at).getTime();
      return Number.isFinite(createdAt) && createdAt >= cutoff;
    });

    const buildMetrics = (items) => {
      const realized = items.filter(row => row.profit_loss != null);
      const totalPnl = realized.reduce((sum, row) => sum + (toNumber(row.profit_loss) ?? 0), 0);
      const wins = realized.filter(row => (toNumber(row.profit_loss) ?? 0) > 0);
      const returns = realized.map(row => {
        const explicit = toNumber(row.return_pct);
        if (explicit != null) return explicit;
        const pnl = toNumber(row.profit_loss);
        const entry = toNumber(row.entry_price);
        const qty = toNumber(row.quantity);
        const basis = entry != null && qty != null ? Math.abs(entry * qty) : null;
        if (pnl == null || basis == null || basis === 0) return null;
        return (pnl / basis) * 100;
      }).filter(value => value != null);
      const avgReturn = returns.length ? returns.reduce((sum, value) => sum + value, 0) / returns.length : null;
      return {
        trades: realized.length,
        total_trades: items.length,
        realized_trades: realized.length,
        win_rate: realized.length ? (wins.length / realized.length) * 100 : null,
        total_pnl: totalPnl,
        avg_return_pct: avgReturn,
      };
    };

    const rulesById = rules.reduce((acc, rule) => {
      acc[rule.id] = rule;
      return acc;
    }, {});

    const byRule = {};
    const byRuleSymbol = {};
    const byRuleWatchlist = {};
    const watchlistEntries = Object.entries(watchlistSymbolMap || {}).map(([id, list]) => [
      Number(id),
      new Set(list || []),
    ]);

    for (const exec of filtered) {
      if (exec.rule_set_id != null) {
        byRule[exec.rule_set_id] = byRule[exec.rule_set_id] || [];
        byRule[exec.rule_set_id].push(exec);

        const symbolKey = `${exec.rule_set_id}::${exec.ticker_symbol}`;
        byRuleSymbol[symbolKey] = byRuleSymbol[symbolKey] || [];
        byRuleSymbol[symbolKey].push(exec);

        for (const [watchlistId, symbolSet] of watchlistEntries) {
          if (exec.ticker_symbol && symbolSet.has(exec.ticker_symbol)) {
            const watchlistKey = `${exec.rule_set_id}::${watchlistId}`;
            byRuleWatchlist[watchlistKey] = byRuleWatchlist[watchlistKey] || [];
            byRuleWatchlist[watchlistKey].push(exec);
          }
        }
      }
    }

    const byRuleMetrics = Object.entries(byRule)
      .map(([ruleSetId, items]) => ({
        rule_set_id: Number(ruleSetId),
        rule_name: rulesById[ruleSetId]?.name || null,
        ...buildMetrics(items),
      }))
      .filter((row) => (row.realized_trades ?? row.trades ?? 0) > 0);

    const byRuleSymbolMetrics = Object.entries(byRuleSymbol)
      .map(([key, items]) => {
        const [ruleSetId, tickerSymbol] = key.split('::');
        return {
          rule_set_id: Number(ruleSetId),
          rule_name: rulesById[ruleSetId]?.name || null,
          ticker_symbol: tickerSymbol,
          ...buildMetrics(items),
        };
      })
      .filter((row) => (row.realized_trades ?? row.trades ?? 0) > 0);

    const byRuleWatchlistMetrics = Object.entries(byRuleWatchlist)
      .map(([key, items]) => {
        const [ruleSetId, watchlistId] = key.split('::');
        return {
          rule_set_id: Number(ruleSetId),
          rule_name: rulesById[ruleSetId]?.name || null,
          watchlist_id: Number(watchlistId),
          ...buildMetrics(items),
        };
      })
      .filter((row) => (row.realized_trades ?? row.trades ?? 0) > 0);

    return {
      overall: buildMetrics(filtered),
      by_rule: byRuleMetrics,
      by_rule_symbol: byRuleSymbolMetrics,
      by_rule_watchlist: byRuleWatchlistMetrics,
    };
  }, [perfRecords, perfRange, rules, watchlistSymbolMap]);

  const perfData = useMemo(() => {
    const ruleIdByName = new Map((rules || []).map((rule) => [String(rule?.name || '').trim().toLowerCase(), rule?.id]));
    const coalesceMetric = (primary, secondary) => (primary == null ? secondary : primary);
    const canonicalRuleId = (row) => {
      if (row?.rule_set_id != null && row?.rule_set_id !== '') return String(row.rule_set_id);
      const name = String(row?.rule_name || '').trim();
      if (!name) return '';
      const byName = ruleIdByName.get(name.toLowerCase());
      if (byName != null) return String(byName);
      const m = name.match(/^Rule\s*#\s*(\d+)$/i);
      if (m) return m[1];
      return '';
    };
    const mergeMetricRows = (rows) => {
      const grouped = new Map();
      rows.forEach((row) => {
        const ruleId = canonicalRuleId(row);
        const ruleName = String(row?.rule_name || '').trim();
        const key = ruleId || `name:${ruleName.toLowerCase()}`;
        const existing = grouped.get(key);
        const trades = toNumber(row?.trades) ?? 0;
        const totalTrades = toNumber(row?.total_trades) ?? trades;
        const realizedTrades = toNumber(row?.realized_trades) ?? 0;
        const totalPnl = toNumber(row?.total_pnl) ?? 0;
        const avgReturn = toNumber(row?.avg_return_pct);
        const weightedReturn = avgReturn == null ? 0 : avgReturn * realizedTrades;
        const wins = row?.__wins != null
          ? (toNumber(row.__wins) ?? 0)
          : (() => {
            const winRate = toNumber(row?.win_rate);
            if (winRate == null) return 0;
            return (winRate * realizedTrades) / 100;
          })();
        if (!existing) {
          grouped.set(key, {
            ...row,
            trades,
            total_trades: totalTrades,
            realized_trades: realizedTrades,
            total_pnl: totalPnl,
            __wins: wins,
            __weighted_return: weightedReturn,
          });
          return;
        }
        existing.trades += trades;
        existing.total_trades += totalTrades;
        existing.realized_trades += realizedTrades;
        existing.total_pnl += totalPnl;
        existing.__wins += wins;
        existing.__weighted_return += weightedReturn;
        if (!existing.rule_name && ruleName) existing.rule_name = ruleName;
        if ((existing.rule_set_id == null || existing.rule_set_id === '') && row?.rule_set_id != null) {
          existing.rule_set_id = row.rule_set_id;
        }
      });

      return Array.from(grouped.values()).map((row) => {
        const realizedTrades = toNumber(row.realized_trades) ?? 0;
        const wins = toNumber(row.__wins) ?? 0;
        const weightedReturn = toNumber(row.__weighted_return) ?? 0;
        return {
          ...row,
          trades: realizedTrades,
          win_rate: realizedTrades > 0 ? (wins / realizedTrades) * 100 : null,
          avg_return_pct: realizedTrades > 0 ? weightedReturn / realizedTrades : null,
          __wins: undefined,
          __weighted_return: undefined,
        };
      });
    };

    const mergeRows = (apiRows, derivedRows, keyForRow) => {
      const apiList = Array.isArray(apiRows) ? apiRows : [];
      const derivedList = Array.isArray(derivedRows) ? derivedRows : [];
      if (!apiList.length) return derivedList;
      const derivedMap = new Map(derivedList.map((row) => [keyForRow(row), row]));
      const usedKeys = new Set();
      const mergedApi = apiList.map((row) => {
        const key = keyForRow(row);
        usedKeys.add(key);
        const fallback = derivedMap.get(key) || {};
        return {
          ...fallback,
          ...row,
          trades: coalesceMetric(fallback.trades, row.trades),
          realized_trades: coalesceMetric(fallback.realized_trades, row.realized_trades),
          win_rate: coalesceMetric(fallback.win_rate, row.win_rate),
          total_pnl: coalesceMetric(fallback.total_pnl, row.total_pnl),
          avg_return_pct: coalesceMetric(fallback.avg_return_pct, row.avg_return_pct),
        };
      });
      const remainingDerived = derivedList.filter((row) => !usedKeys.has(keyForRow(row)));
      return mergedApi.concat(remainingDerived);
    };

    if (!performance) return derivedPerformance;
    const dedupedByRule = mergeMetricRows(derivedPerformance.by_rule || []).filter(
      (row) => (toNumber(row?.realized_trades) ?? 0) > 0
    );
    const mergedByRuleSymbol = mergeRows(
      performance.by_rule_symbol,
      derivedPerformance.by_rule_symbol,
      (row) => `${row?.rule_set_id ?? ''}::${row?.ticker_symbol ?? ''}`
    ).filter((row) => (toNumber(row?.realized_trades) ?? 0) > 0);
    const mergedByRuleWatchlist = mergeRows(
      performance.by_rule_watchlist,
      derivedPerformance.by_rule_watchlist,
      (row) => `${row?.rule_set_id ?? ''}::${row?.watchlist_id ?? ''}`
    ).filter((row) => (toNumber(row?.realized_trades) ?? 0) > 0);
    const apiOverall = performance.overall || {};
    const derivedOverall = derivedPerformance.overall || {};
    const hasDerivedOverallData =
      (toNumber(derivedOverall.total_trades) ?? toNumber(derivedOverall.trades) ?? 0) > 0;
    const mergedOverall = {
      ...apiOverall,
      ...(hasDerivedOverallData ? derivedOverall : {}),
      trades: hasDerivedOverallData
        ? (toNumber(derivedOverall.trades) ?? 0)
        : coalesceMetric(apiOverall.trades, derivedOverall.trades),
      total_trades: hasDerivedOverallData
        ? (toNumber(derivedOverall.total_trades) ?? toNumber(derivedOverall.trades) ?? 0)
        : coalesceMetric(apiOverall.total_trades, derivedOverall.total_trades),
      realized_trades: hasDerivedOverallData
        ? (toNumber(derivedOverall.realized_trades) ?? toNumber(derivedOverall.trades) ?? 0)
        : coalesceMetric(apiOverall.realized_trades, derivedOverall.realized_trades),
      total_pnl: hasDerivedOverallData
        ? (toNumber(derivedOverall.total_pnl) ?? 0)
        : coalesceMetric(apiOverall.total_pnl, derivedOverall.total_pnl),
      win_rate: hasDerivedOverallData
        ? coalesceMetric(derivedOverall.win_rate, 0)
        : coalesceMetric(apiOverall.win_rate, derivedOverall.win_rate),
      avg_return_pct: hasDerivedOverallData
        ? derivedOverall.avg_return_pct
        : coalesceMetric(apiOverall.avg_return_pct, derivedOverall.avg_return_pct),
    };

    return {
      overall: mergedOverall,
      by_rule: dedupedByRule,
      by_rule_symbol: mergedByRuleSymbol,
      by_rule_watchlist: mergedByRuleWatchlist,
    };
  }, [performance, derivedPerformance, rules]);

  const hasPerfData = useMemo(() => {
    if (!perfData) return false;
    return (
      (perfData.overall?.trades || 0) > 0 ||
      (perfData.by_rule || []).length > 0 ||
      (perfData.by_rule_symbol || []).length > 0 ||
      (perfData.by_rule_watchlist || []).length > 0
    );
  }, [perfData]);

  const byRuleSymbolGroups = useMemo(() => {
    const rows = perfData?.by_rule_symbol || [];
    if (!rows.length) return [];
    const groups = {};
    rows.forEach((row) => {
      const ruleId = Number(row.rule_set_id);
      if (!groups[ruleId]) {
        groups[ruleId] = {
          rule_set_id: ruleId,
          rule_name: row.rule_name || `Rule #${ruleId}`,
          rows: [],
        };
      }
      groups[ruleId].rows.push(row);
    });
    return Object.values(groups)
      .map((group) => ({
        ...group,
        rows: group.rows.slice().sort((a, b) => {
          const pnlA = Number(a.total_pnl || 0);
          const pnlB = Number(b.total_pnl || 0);
          if (pnlB !== pnlA) return pnlB - pnlA;
          return String(a.ticker_symbol || '').localeCompare(String(b.ticker_symbol || ''));
        }),
      }))
      .sort((a, b) => String(a.rule_name || '').localeCompare(String(b.rule_name || '')));
  }, [perfData]);

  const byRuleWatchlistGroups = useMemo(() => {
    const rows = perfData?.by_rule_watchlist || [];
    if (!rows.length) return [];
    const groups = {};
    rows.forEach((row) => {
      const ruleId = Number(row.rule_set_id);
      if (!groups[ruleId]) {
        groups[ruleId] = {
          rule_set_id: ruleId,
          rule_name: row.rule_name || `Rule #${ruleId}`,
          rows: [],
        };
      }
      groups[ruleId].rows.push(row);
    });
    return Object.values(groups)
      .map((group) => ({
        ...group,
        rows: group.rows.slice().sort((a, b) => {
          const pnlA = Number(a.total_pnl || 0);
          const pnlB = Number(b.total_pnl || 0);
          if (pnlB !== pnlA) return pnlB - pnlA;
          const nameA = a.watchlist_name || watchlistNameMap[a.watchlist_id] || `Watchlist #${a.watchlist_id}`;
          const nameB = b.watchlist_name || watchlistNameMap[b.watchlist_id] || `Watchlist #${b.watchlist_id}`;
          return String(nameA).localeCompare(String(nameB));
        }),
      }))
      .sort((a, b) => String(a.rule_name || '').localeCompare(String(b.rule_name || '')));
  }, [perfData, watchlistNameMap]);

  const [form, setForm] = useState({
    name: '',
    action: 'BUY',
    quantity: 1,
    quantity_type: 'SHARES',
    logic_operator: 'AND',
    cooldown_minutes: 60,
    max_holdings_per_rule: 10,
    stop_loss_pct: '',
    take_profit_pct: '',
    targetType: 'SYMBOL',
          watchlist_ids: [],    selectedSymbols: [],
    conditions: [{ ...DEFAULT_CONDITION }],
    is_active: true,
  });

  useEffect(() => {
    if (!profileId || !token) return;
    fetchRules();
    fetchWatchlists();
    fetchExecutions();
    fetchAutoTrades();
    fetchPerformance(perfRange);
    fetchPauseState();
    fetchExecutionConfig();
    fetchMarketHoursDecision();
  }, [profileId, token]);

  useEffect(() => {
    if (!profileId || !token) return;
    const intervalId = setInterval(() => {
      fetchRules();
    }, (AUTO_TRADING_CONFIG.rulesRefreshMinutes || 30) * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [profileId, token]);

  const fetchRules = async () => {
    if (!profileId) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/auto-trading/profiles/${profileId}/rules`, { headers });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to load auto-trading rules');
      const rows = Array.isArray(data) ? data : [];
      setRules(rows);
      // Keep symbol selector usable even when watchlist endpoints are empty/unavailable.
      const symbolsFromRules = new Set();
      rows.forEach(rule => {
        (rule?.targets || []).forEach(target => {
          const symbol = (target?.ticker_symbol || target?.tickerSymbol || '').toUpperCase();
          if (symbol) symbolsFromRules.add(symbol);
        });
      });
      if (symbolsFromRules.size > 0) {
        setSymbols(prev => Array.from(new Set([...(prev || []), ...symbolsFromRules])).sort());
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPauseState = async () => {
    if (!profileId) return;
    try {
      const response = await fetch(`${API_BASE}/api/auto-trading/profiles/${profileId}/pause-state`, { headers });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to load pause state');
      setPauseState({
        all: !!data.all,
        rules: Array.isArray(data.rules) ? data.rules : [],
        symbols: Array.isArray(data.symbols) ? data.symbols : [],
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fetchExecutionConfig = async () => {
    if (!profileId) return;
    setLoadingGap(true);
    try {
      const response = await fetch(`${API_BASE}/api/auto-trading/profiles/${profileId}/execution-config`, { headers });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to load execution config');
      const minutes = Number(data.rule_gap_minutes);
      if (Number.isFinite(minutes) && minutes >= 0) {
        setRuleGapMinutes(minutes);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingGap(false);
    }
  };

  const saveExecutionConfig = async () => {
    if (!profileId) return;
    const value = Number(ruleGapMinutes);
    if (!Number.isFinite(value) || value < 0) {
      toast.error('Rule gap minutes must be 0 or greater');
      return;
    }
    setSavingGap(true);
    try {
      const response = await fetch(`${API_BASE}/api/auto-trading/profiles/${profileId}/execution-config`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ rule_gap_minutes: value }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to update execution config');
      const minutes = Number(data.rule_gap_minutes);
      if (Number.isFinite(minutes) && minutes >= 0) {
        setRuleGapMinutes(minutes);
      }
      toast.success('Execution gap updated');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingGap(false);
    }
  };

  const fetchMarketHoursDecision = async () => {
    setLoadingMarketHours(true);
    try {
      const response = await fetch(`${API_BASE}/api/stock-market/fmp/exchange-market-hours`, { headers });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to load market-hours state');
      setMarketHoursDecision(data);
      const mode = String(data?.mode || 'auto').toUpperCase();
      if (mode === 'OVERRIDE_OPEN') setMarketHoursMode('OPEN');
      else if (mode === 'OVERRIDE_CLOSED') setMarketHoursMode('CLOSED');
      else setMarketHoursMode('AUTO');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingMarketHours(false);
    }
  };

  const saveMarketHoursOverride = async () => {
    setSavingMarketHours(true);
    try {
      const response = await fetch(`${API_BASE}/api/stock-market/fmp/exchange-market-hours/override`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ mode: marketHoursMode }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to update market-hours override');
      setMarketHoursDecision(data);
      toast.success(`Market-hours mode set to ${marketHoursMode}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingMarketHours(false);
    }
  };

  const clearMarketHoursOverride = async () => {
    setSavingMarketHours(true);
    try {
      const response = await fetch(`${API_BASE}/api/stock-market/fmp/exchange-market-hours/override`, {
        method: 'DELETE',
        headers,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to clear market-hours override');
      setMarketHoursMode('AUTO');
      setMarketHoursDecision(data);
      toast.success('Market-hours override cleared');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingMarketHours(false);
    }
  };

  const fetchExecutions = async () => {
    if (!profileId) return;
    setLoadingExec(true);
    try {
      const response = await fetch(`${API_BASE}/api/auto-trading/profiles/${profileId}/executions?limit=100`, { headers });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to load executions');
      setExecutions(Array.isArray(data) ? data.map(normalizeExecutionShape) : []);
      fetchPerformance(perfRange);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingExec(false);
    }
  };

  const fetchAutoTrades = async () => {
    if (!profileId) return;
    try {
      const pageSize = 200;
      let offset = 0;
      let allTrades = [];
      while (true) {
        const response = await fetch(`${API_BASE}/api/paper-trading/profiles/${profileId}/trades?limit=${pageSize}&offset=${offset}`, { headers });
        if (!response.ok) break;
        const data = await response.json();
        const batch = Array.isArray(data) ? data.map(normalizeTradeShape) : [];
        if (batch.length === 0) break;
        allTrades = allTrades.concat(batch);
        if (batch.length < pageSize) break;
        offset += pageSize;
      }
      const autoFiltered = allTrades.filter(trade => {
        const orderType = trade.order_type || '';
        const closeReason = trade.close_reason || '';
        const details = trade.details || '';
        const hasRuleMapping = trade.rule_set_id != null;
        const isAutoOrder = orderType.toUpperCase().includes('AUTO');
        const isAutoReason = closeReason.toUpperCase().includes('AUTO');
        const isRiskExit = closeReason.toUpperCase().includes('TAKE_PROFIT') || closeReason.toUpperCase().includes('STOP_LOSS');
        const isAutoDetails = details.toUpperCase().includes('AUTO');
        const isAuto = hasRuleMapping || isAutoOrder || isAutoReason || isRiskExit || isAutoDetails;
        return isAuto;
      });
      setAutoTrades(autoFiltered);
    } catch (error) {
      console.error('Failed to fetch auto trades:', error);
    }
  };

  const fetchPerformance = async (rangeValue) => {
    if (!profileId) return;
    setLoadingPerf(true);
    try {
      const windowDays = rangeValue === 'all' ? '' : `window_days=${rangeValue}`;
      const response = await fetch(
        `${API_BASE}/api/auto-trading/profiles/${profileId}/performance${windowDays ? `?${windowDays}` : ''}`,
        { headers }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to load performance');
      setPerformance(normalizePerformancePayload(data));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingPerf(false);
    }
  };

  const fetchWatchlists = async () => {
    if (!profileId) return;
    try {
      const watchlistsRes = await fetch(`${API_BASE}/api/stock-market/profiles/${profileId}/watchlists`, { headers });
      const watchlistsData = watchlistsRes.ok ? await watchlistsRes.json() : [];
      const list = Array.isArray(watchlistsData) ? watchlistsData : [];
      setWatchlists(list);
      setWatchlistNameMap(list.reduce((acc, item) => {
        acc[item.id] = item.name || `Watchlist #${item.id}`;
        return acc;
      }, {}));
      const allSymbols = new Set();
      const nextSymbolNameMap = {};
      const nextWatchlistSymbolMap = {};
      for (const watchlist of list) {
        const res = await fetch(`${API_BASE}/api/stock-market/watchlists/${watchlist.id}/stocks`, { headers });
        if (!res.ok) continue;
        const payload = await res.json();
        const items = Array.isArray(payload) ? payload : [];
        nextWatchlistSymbolMap[watchlist.id] = [];
        items.forEach(item => {
          const symbol = item.stock?.ticker_symbol || item.ticker_symbol;
          if (symbol) allSymbols.add(symbol);
          if (symbol) nextWatchlistSymbolMap[watchlist.id].push(symbol);
          if (symbol && item.stock?.company_name) {
            nextSymbolNameMap[symbol] = item.stock.company_name;
          }
        });
      }

      // Fallback for legacy profile watchlist data.
      if (allSymbols.size === 0) {
        const legacyRes = await fetch(`${API_BASE}/api/stock-market/profiles/${profileId}/watchlist`, { headers });
        if (legacyRes.ok) {
          const legacyPayload = await legacyRes.json();
          const legacyItems = Array.isArray(legacyPayload) ? legacyPayload : [];
          legacyItems.forEach(item => {
            const symbol = item.stock?.ticker_symbol || item.ticker_symbol;
            if (symbol) allSymbols.add(symbol);
            if (symbol && item.stock?.company_name) {
              nextSymbolNameMap[symbol] = item.stock.company_name;
            } else if (symbol && item.company_name) {
              nextSymbolNameMap[symbol] = item.company_name;
            }
          });
        }
      }

      setSymbols(Array.from(allSymbols).sort());
      setSymbolNameMap(nextSymbolNameMap);
      setWatchlistSymbolMap(nextWatchlistSymbolMap);
    } catch (error) {
      console.error('Failed to fetch watchlists:', error);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addSymbol = (value) => {
    const normalized = value.trim().toUpperCase();
    if (!normalized) return;
    setForm(prev => ({
      ...prev,
      selectedSymbols: prev.selectedSymbols.includes(normalized)
        ? prev.selectedSymbols
        : [...prev.selectedSymbols, normalized],
    }));
  };

  const removeSymbol = (symbol) => {
    setForm(prev => ({
      ...prev,
      selectedSymbols: prev.selectedSymbols.filter(item => item !== symbol),
    }));
  };

  const addCondition = () => {
    setForm(prev => ({ ...prev, conditions: [...prev.conditions, { ...DEFAULT_CONDITION }] }));
  };

  const updateCondition = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      conditions: prev.conditions.map((condition, idx) => (
        idx === index ? { ...condition, [field]: value } : condition
      )),
    }));
  };

  const updateConditionParam = (index, param, value) => {
    setForm(prev => ({
      ...prev,
      conditions: prev.conditions.map((condition, idx) => (
        idx === index
          ? { ...condition, params: { ...condition.params, [param]: value } }
          : condition
      )),
    }));
  };

  const removeCondition = (index) => {
    setForm(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, idx) => idx !== index),
    }));
  };

  const buildTargetsPayload = () => {
    if (form.targetType === 'WATCHLIST') {
      return form.watchlist_ids.map(id => ({ target_type: 'WATCHLIST', watchlist_id: Number(id) }));
    }
    return form.selectedSymbols.map(symbol => ({ target_type: 'SYMBOL', ticker_symbol: symbol }));
  };

  const buildPayload = () => ({
    name: form.name ? form.name.toUpperCase() : null,
    action: form.action,
    quantity: Number(form.quantity),
    quantity_type: form.quantity_type,
    logic_operator: form.logic_operator,
    cooldown_minutes: Number(form.cooldown_minutes) || 60,
    max_holdings_per_rule: Number(form.max_holdings_per_rule) || 10,
    stop_loss_pct: form.stop_loss_pct === '' ? null : Number(form.stop_loss_pct),
    take_profit_pct: form.take_profit_pct === '' ? null : Number(form.take_profit_pct),
    targets: buildTargetsPayload(),
    conditions: form.conditions.map(condition => {
      const validWindows = WINDOW_OPTIONS.map(opt => opt.value);
      const timeWindow = validWindows.includes(condition.time_window) ? condition.time_window : '5min';
      return {
        condition_type: condition.condition_type,
        threshold_value: condition.threshold_value === '' ? null : Number(condition.threshold_value),
        time_window: timeWindow,
        params: condition.params || {},
      };
    }),
    is_active: form.is_active,
  });

  const resetForm = () => {
    setForm({
      name: '',
      action: 'BUY',
      quantity: 1,
      quantity_type: 'SHARES',
      logic_operator: 'AND',
      cooldown_minutes: 60,
      max_holdings_per_rule: 10,
      stop_loss_pct: '',
      take_profit_pct: '',
      targetType: 'SYMBOL',
      watchlist_ids: [],
      selectedSymbols: [],
      conditions: [{ ...DEFAULT_CONDITION }],
      is_active: true,
    });
  };

  const validationErrors = () => {
    const issues = [];
    if (!form.name.trim()) issues.push('Rule name is empty.');
    if (form.targetType === 'WATCHLIST' && form.watchlist_ids.length === 0) {
      issues.push('Select a watchlist target.');
    }
    if (form.targetType === 'SYMBOL' && form.selectedSymbols.length === 0) {
      issues.push('Add at least one symbol.');
    }
    if (!form.conditions || form.conditions.length === 0) {
      issues.push('Add at least one condition.');
    } else {
      form.conditions.forEach((condition, idx) => {
        if (condition.threshold_value === '' || condition.threshold_value == null) {
          const meta = CONDITION_OPTIONS.find(opt => opt.value === condition.condition_type);
          if (meta?.threshold) {
            issues.push(`Condition ${idx + 1} needs a threshold.`);
          }
        }
      });
    }
    return issues;
  };

  const handleCreate = async () => {
    if (!profileId) return;
    if (form.targetType === 'WATCHLIST' && form.watchlist_ids.length === 0) {
      toast.error('Select a watchlist');
      return;
    }
    if (form.targetType === 'SYMBOL' && form.selectedSymbols.length === 0) {
      toast.error('Add at least one symbol');
      return;
    }
    setSaving(true);
    const payload = buildPayload();
    console.log('Creating rule with payload:', JSON.stringify(payload, null, 2));
    try {
      const response = await fetch(`${API_BASE}/api/auto-trading/profiles/${profileId}/rules`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      console.log('Response:', response.status, data);
      if (!response.ok) throw new Error(data.detail || 'Failed to create rule');
      toast.success('Auto-trade rule created');
      resetForm();
      fetchRules();
      fetchExecutions();
      fetchPerformance(perfRange);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      name: (rule.name || '').toUpperCase(),
      action: rule.action || 'BUY',
      quantity: rule.quantity || 1,
      quantity_type: rule.quantity_type || 'SHARES',
      logic_operator: rule.logic_operator || 'AND',
      cooldown_minutes: rule.cooldown_minutes || 60,
      max_holdings_per_rule: rule.max_holdings_per_rule || 10,
      stop_loss_pct: rule.stop_loss_pct ?? '',
      take_profit_pct: rule.take_profit_pct ?? '',
      targetType: rule.targets?.[0]?.target_type || 'SYMBOL',
      watchlist_ids: (rule.targets || []).filter(t => t.target_type === 'WATCHLIST').map(t => t.watchlist_id),
      selectedSymbols: (rule.targets || []).filter(t => t.target_type === 'SYMBOL').map(t => t.ticker_symbol),
      conditions: (rule.conditions || []).map(c => {
        const validWindows = WINDOW_OPTIONS.map(opt => opt.value);
        return {
          condition_type: c.condition_type,
          threshold_value: c.threshold_value ?? '',
          time_window: validWindows.includes(c.time_window) ? c.time_window : '5min',
          params: c.params || {},
        };
      }),
      is_active: rule.is_active ?? true,
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editingRule) return;
    setSaving(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/auto-trading/profiles/${profileId}/rules/${editingRule.id}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(buildPayload()),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to update rule');
      toast.success('Rule updated');
      setShowEditModal(false);
      setEditingRule(null);
      resetForm();
      fetchRules();
      fetchExecutions();
      fetchPerformance(perfRange);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleRule = async (rule) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/auto-trading/profiles/${profileId}/rules/${rule.id}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ is_active: !rule.is_active }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to update rule');
      setRules(prev => prev.map(item => (item.id === rule.id ? data : item)));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm('Delete this auto-trade rule?')) return;
    try {
      const response = await fetch(
        `${API_BASE}/api/auto-trading/profiles/${profileId}/rules/${ruleId}`,
        { method: 'DELETE', headers }
      );
      if (!response.ok) throw new Error('Failed to delete rule');
      setRules(prev => prev.filter(rule => rule.id !== ruleId));
      fetchExecutions();
      fetchPerformance(perfRange);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const bypassCooldown = async (ruleId, symbol) => {
    if (!profileId || !ruleId || !symbol) return;
    try {
      const response = await fetch(
        `${API_BASE}/api/auto-trading/profiles/${profileId}/rules/${ruleId}/cooldown-bypass`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ symbols: [symbol] }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to request cooldown bypass');
      toast.success(`Cooldown bypass requested for ${symbol}`);
      fetchRules();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const updatePauseState = async (action, scope, options = {}) => {
    if (!profileId) return;
    try {
      const response = await fetch(
        `${API_BASE}/api/auto-trading/profiles/${profileId}/${action}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            scope,
            rule_id: options.ruleId ?? null,
            symbol: options.symbol ?? null,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || `Failed to ${action} auto-trade`);
      setPauseState({
        all: !!data.all,
        rules: Array.isArray(data.rules) ? data.rules : [],
        symbols: Array.isArray(data.symbols) ? data.symbols : [],
      });
      toast.success(`Auto-trade ${action} updated`);
      fetchRules();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const isRulePaused = (ruleId) => pauseState.rules.includes(Number(ruleId));
  const isSymbolPaused = (symbol) => pauseState.symbols.includes(String(symbol || '').toUpperCase());

  const renderConditionRow = (condition, index) => {
    const meta = CONDITION_OPTIONS.find(opt => opt.value === condition.condition_type);
    return (
      <div key={`${condition.condition_type}-${index}`} className="auto-trade-cond-row mb-3">
        <Row className="g-2 align-items-end">
          <Col md={4}>
            <Form.Label>Condition</Form.Label>
            <Form.Select
              value={condition.condition_type}
              onChange={(e) => updateCondition(index, 'condition_type', e.target.value)}
            >
              {CONDITION_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Col>
          {meta?.threshold && (
            <Col md={3}>
              <Form.Label>Threshold</Form.Label>
              <Form.Control
                type="number"
                value={condition.threshold_value}
                onChange={(e) => updateCondition(index, 'threshold_value', e.target.value)}
              />
            </Col>
          )}
          {meta?.window && (
            <Col md={3}>
              <Form.Label>Window</Form.Label>
              <Form.Select
                value={condition.time_window}
                onChange={(e) => updateCondition(index, 'time_window', e.target.value)}
              >
                {WINDOW_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Form.Select>
            </Col>
          )}
          {meta?.params?.includes('period') && (
            <Col md={2}>
              <Form.Label>Period</Form.Label>
              <Form.Control
                type="number"
                value={condition.params?.period || 14}
                onChange={(e) => updateConditionParam(index, 'period', e.target.value)}
              />
            </Col>
          )}
          {meta?.params?.includes('fast') && (
            <Col md={2}>
              <Form.Label>Fast</Form.Label>
              <Form.Control
                type="number"
                value={condition.params?.fast || 50}
                onChange={(e) => updateConditionParam(index, 'fast', e.target.value)}
              />
            </Col>
          )}
          {meta?.params?.includes('slow') && (
            <Col md={2}>
              <Form.Label>Slow</Form.Label>
              <Form.Control
                type="number"
                value={condition.params?.slow || 200}
                onChange={(e) => updateConditionParam(index, 'slow', e.target.value)}
              />
            </Col>
          )}
          <Col md={2} className="text-end">
            <Button variant="outline-danger" size="sm" onClick={() => removeCondition(index)}>
              Remove
            </Button>
          </Col>
        </Row>
      </div>
    );
  };

  const hasValidationErrors = validationErrors().length > 0;
  const RuleForm = (
    <>
      <Row className="g-3">
        <Col md={6}>
          <Form.Label>Rule Name</Form.Label>
          <Form.Control
            value={form.name}
            onChange={(e) => handleFormChange('name', e.target.value.toUpperCase())}
            placeholder="Momentum breakout"
          />
        </Col>
        <Col md={3}>
          <Form.Label>Action</Form.Label>
          <Form.Select value={form.action} onChange={(e) => handleFormChange('action', e.target.value)}>
            <option value="BUY">Buy</option>
            <option value="SELL">Sell</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label>Logic</Form.Label>
          <Form.Select value={form.logic_operator} onChange={(e) => handleFormChange('logic_operator', e.target.value)}>
            <option value="AND">AND</option>
            <option value="OR">OR</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label>Quantity</Form.Label>
          <Form.Control
            type="number"
            min="1"
            value={form.quantity}
            onChange={(e) => handleFormChange('quantity', e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Label>Cooldown (min)</Form.Label>
          <Form.Control
            type="number"
            min="1"
            value={form.cooldown_minutes}
            onChange={(e) => handleFormChange('cooldown_minutes', e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Label>Max Holdings</Form.Label>
          <Form.Control
            type="number"
            min="1"
            value={form.max_holdings_per_rule}
            onChange={(e) => handleFormChange('max_holdings_per_rule', e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Label>Stop Loss %</Form.Label>
          <Form.Control
            type="number"
            min="0"
            value={form.stop_loss_pct}
            onChange={(e) => handleFormChange('stop_loss_pct', e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Label>Take Profit %</Form.Label>
          <Form.Control
            type="number"
            min="0"
            value={form.take_profit_pct}
            onChange={(e) => handleFormChange('take_profit_pct', e.target.value)}
          />
        </Col>
      </Row>

      <hr />

      <Row className="g-3">
        <Col md={4}>
          <Form.Label>Target Type</Form.Label>
          <Form.Select
            value={form.targetType}
            onChange={(e) => handleFormChange('targetType', e.target.value)}
          >
            <option value="SYMBOL">Selected Symbols</option>
            <option value="WATCHLIST">Watchlist</option>
          </Form.Select>
        </Col>
        {form.targetType === 'WATCHLIST' && (
          <Col md={8}>
            <Form.Label>Watchlist</Form.Label>
            <Form.Select
              multiple
              value={form.watchlist_ids}
              onChange={(e) => handleFormChange('watchlist_ids', Array.from(e.target.selectedOptions, option => option.value))}
            >
              <option value="">Select watchlist</option>
              {watchlists.map(watchlist => (
                <option key={watchlist.id} value={watchlist.id}>{watchlist.name}</option>
              ))}
            </Form.Select>
          </Col>
        )}
        {form.targetType === 'SYMBOL' && (
          <Col md={8}>
            <Form.Label>Add Symbols</Form.Label>
            <div className="d-flex gap-2">
              <Form.Select
                value=""
                onChange={(e) => addSymbol(e.target.value)}
              >
                <option value="">Select symbol</option>
                {symbols.map(symbol => (
                  <option key={symbol} value={symbol}>{symbol}</option>
                ))}
              </Form.Select>
              <Form.Control
                placeholder="Type symbol"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSymbol(e.currentTarget.value);
                    e.currentTarget.value = '';
                  }
                }}
              />
            </div>
            <div className="auto-trade-tags mt-2">
              {form.selectedSymbols.map(symbol => (
                <span key={symbol} className="auto-trade-tag">
                  {symbol}
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 ms-2"
                    onClick={() => removeSymbol(symbol)}
                  >
                    ×
                  </Button>
                </span>
              ))}
            </div>
          </Col>
        )}
      </Row>

      <hr />

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <div className="auto-trade-section-label">Conditions</div>
          <div className="auto-trade-muted">Add multiple signals and combine with AND/OR logic.</div>
        </div>
        <Button variant="outline-primary" size="sm" onClick={addCondition}>
          + Add Condition
        </Button>
      </div>
      {form.conditions.length === 0 ? (
        <div className="auto-trade-empty">No conditions added yet.</div>
      ) : (
        form.conditions.map(renderConditionRow)
      )}
    </>
  );

  return (
    <div className="auto-trade-wrapper">
      <div className="auto-trade-hero mb-4">
        <h3>Auto Trading Studio</h3>
        <p>Design rule-based trades with risk controls and watchlist targeting.</p>
      </div>

      <Row className="g-4">
        <Col lg={12}>
          <Card className="auto-trade-card">
            <Card.Body>
              <div className="d-flex justify-content-end align-items-end gap-2 mb-3">
                <div style={{ minWidth: '240px' }}>
                  <Form.Label className="mb-1">Rule Execution Gap (minutes)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={ruleGapMinutes}
                    onChange={(e) => setRuleGapMinutes(e.target.value)}
                    disabled={loadingGap || savingGap}
                  />
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mb-1"
                  onClick={saveExecutionConfig}
                  disabled={loadingGap || savingGap}
                >
                  {savingGap ? 'Saving...' : 'Save Gap'}
                </Button>
              </div>
              <div className="d-flex justify-content-end align-items-end gap-2 mb-3">
                <div style={{ minWidth: '280px' }}>
                  <Form.Label className="mb-1">Market Hours Override (Testing)</Form.Label>
                  <Form.Select
                    value={marketHoursMode}
                    onChange={(e) => setMarketHoursMode(e.target.value)}
                    disabled={loadingMarketHours || savingMarketHours}
                  >
                    <option value="AUTO">Auto (FMP)</option>
                    <option value="OPEN">Force Open</option>
                    <option value="CLOSED">Force Closed</option>
                  </Form.Select>
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mb-1"
                  onClick={saveMarketHoursOverride}
                  disabled={loadingMarketHours || savingMarketHours}
                >
                  {savingMarketHours ? 'Saving...' : 'Apply'}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mb-1"
                  onClick={clearMarketHoursOverride}
                  disabled={loadingMarketHours || savingMarketHours}
                >
                  Clear Override
                </Button>
              </div>
              <div className="auto-trade-muted text-end mb-3">
                Market can trade: {marketHoursDecision?.can_trade ? 'Yes' : 'No'} · Mode: {marketHoursDecision?.mode || 'auto'} ·
                Reason: {marketHoursDecision?.reason || '—'}
              </div>
              <Tabs defaultActiveKey="builder" className="mb-3 auto-trade-tabs">
                <Tab eventKey="builder" title="Rule Builder">
                  <Row className="g-4">
                    <Col lg={8}>
                      {RuleForm}
                      <div className="d-flex justify-content-end mt-3">
                        <Button variant="primary" onClick={handleCreate} disabled={saving || hasValidationErrors}>
                          {saving ? 'Saving...' : 'Create Rule'}
                        </Button>
                      </div>
                    </Col>
                    <Col lg={4}>
                      <Card className="auto-trade-rule-card">
                        <div className="auto-trade-section-label">Live Preview</div>
                        <div className="auto-trade-rule-title mb-1">
                          {form.name || 'Untitled Rule'}
                        </div>
                        <div className="auto-trade-rule-meta mb-3">
                          {form.action} · {form.quantity} shares · Cooldown {form.cooldown_minutes}m · Max holdings {form.max_holdings_per_rule}
                        </div>
                        <div className="auto-trade-section-label">Targets</div>
                        <div className="auto-trade-tags mb-3">
                          {form.targetType === 'WATCHLIST' ? (
                            (form.watchlist_ids.length > 0
                              ? form.watchlist_ids.map(id => (
                                <span key={id} className="auto-trade-tag">{watchlistNameMap[id] || `Watchlist #${id}`}</span>
                              ))
                              : <span className="auto-trade-tag">Select watchlist</span>)
                          ) : (
                            (form.selectedSymbols.length > 0
                              ? form.selectedSymbols.map(symbol => (
                                <span key={symbol} className="auto-trade-tag">{symbol}</span>
                              ))
                              : <span className="auto-trade-tag">Add symbols</span>)
                          )}
                        </div>
                        <div className="auto-trade-section-label">Risk</div>
                        <div className="auto-trade-muted mb-3">
                          SL {form.stop_loss_pct ? `${form.stop_loss_pct}%` : '—'} ·
                          TP {form.take_profit_pct ? `${form.take_profit_pct}%` : '—'}
                        </div>
                        <div className="auto-trade-section-label">Conditions</div>
                        <div className="auto-trade-tags">
                          {form.conditions.length > 0 ? (
                            form.conditions.map((condition, idx) => (
                              <span key={`${condition.condition_type}-${idx}`} className="auto-trade-tag">
                                {condition.condition_type.replace(/_/g, ' ')}
                                {condition.threshold_value !== '' && condition.threshold_value != null
                                  ? ` ${condition.threshold_value}`
                                  : ''}
                              </span>
                            ))
                          ) : (
                            <span className="auto-trade-tag">Add conditions</span>
                          )}
                        </div>
                        {validationErrors().length > 0 && (
                          <div className="mt-3 auto-trade-validation">
                            <div className="auto-trade-validation-title">Fix these to enable rule creation</div>
                            {validationErrors().map((issue, idx) => (
                              <div key={idx} className="auto-trade-validation-item">
                                • {issue}
                              </div>
                            ))}
                          </div>
                        )}
                      </Card>
                    </Col>
                  </Row>
                </Tab>
                <Tab eventKey="rules" title="Rules">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="auto-trade-section-label mb-0">Active Rules</span>
                    <div className="d-flex gap-2 align-items-center">
                      <span className="auto-trade-pill">{rules.length} rules</span>
                      <Button
                        variant={pauseState.all ? 'outline-success' : 'outline-warning'}
                        size="sm"
                        onClick={() => updatePauseState(pauseState.all ? 'resume' : 'pause', 'ALL')}
                      >
                        {pauseState.all ? 'Resume All' : 'Pause All'}
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={fetchRules}>
                        Refresh
                      </Button>
                    </div>
                  </div>
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : rules.length === 0 ? (
                    <div className="auto-trade-empty">No auto-trading rules yet.</div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {rules.map(rule => (
                        <div key={rule.id} className={`auto-trade-rule-row ${expandedRuleId === rule.id ? 'is-open' : ''}`}>
                          <div
                            className="auto-trade-rule-summary"
                            onClick={() => setExpandedRuleId(prev => (prev === rule.id ? null : rule.id))}
                          >
                            <div className="auto-trade-rule-summary-main">
                              <div className="auto-trade-rule-title">{(rule.name || 'Untitled Rule').toUpperCase()}</div>
                              <div className="auto-trade-rule-meta">
                                {rule.action} · {rule.quantity} shares · Cooldown {rule.cooldown_minutes}m · Max holdings {rule.max_holdings_per_rule || 10} ·
                                Targets {(rule.targets || []).length} · Conditions {(rule.conditions || []).length} ·
                                SL {rule.stop_loss_pct ? `${rule.stop_loss_pct}%` : '—'} ·
                                TP {rule.take_profit_pct ? `${rule.take_profit_pct}%` : '—'}
                              </div>
                            </div>
                            <div className="auto-trade-actions" onClick={(e) => e.stopPropagation()}>
                              <Form.Check
                                type="switch"
                                id={`auto-trade-${rule.id}`}
                                label={rule.is_active ? 'Active' : 'Paused'}
                                checked={rule.is_active}
                                onChange={() => toggleRule(rule)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <Button variant="outline-secondary" size="sm" onClick={() => openEdit(rule)}>
                                Edit
                              </Button>
                              <Button
                                variant={isRulePaused(rule.id) ? 'outline-success' : 'outline-warning'}
                                size="sm"
                                onClick={() => updatePauseState(isRulePaused(rule.id) ? 'resume' : 'pause', 'RULE', { ruleId: rule.id })}
                              >
                                {isRulePaused(rule.id) ? 'Resume Rule' : 'Pause Rule'}
                              </Button>
                              <Button variant="outline-danger" size="sm" onClick={() => deleteRule(rule.id)}>
                                Delete
                              </Button>
                              <span className="auto-trade-rule-chevron">
                                {expandedRuleId === rule.id ? '−' : '+'}
                              </span>
                            </div>
                          </div>
                          {expandedRuleId === rule.id && (
                            <div className="auto-trade-rule-details">
                              <Row className="g-3">
                            <Col md={6}>
                              <div className="auto-trade-section-label">Targets</div>
                              <div className="auto-trade-tags">
                                {(rule.targets || []).map((target, idx) => (
                                  <span key={`${target.target_type}-${idx}`} className="auto-trade-tag">
                                {target.target_type === 'WATCHLIST'
                                  ? (watchlistNameMap[target.watchlist_id] || `Watchlist #${target.watchlist_id}`)
                                  : (symbolNameMap[target.ticker_symbol]
                                    ? `${target.ticker_symbol} · ${symbolNameMap[target.ticker_symbol]}`
                                    : target.ticker_symbol)}
                                  </span>
                                ))}
                              </div>
                            </Col>
                            <Col md={6}>
                              <div className="auto-trade-section-label">Risk</div>
                              <div className="auto-trade-muted">
                                SL {rule.stop_loss_pct ? `${rule.stop_loss_pct}%` : '—'} ·
                                TP {rule.take_profit_pct ? `${rule.take_profit_pct}%` : '—'}
                              </div>
                            </Col>
                            <Col md={12}>
                              <div className="auto-trade-section-label">Conditions</div>
                              <div className="auto-trade-tags">
                                {(rule.conditions || []).map((condition, idx) => (
                                  <span key={`${condition.condition_type}-${idx}`} className="auto-trade-tag">
                                    {condition.condition_type.replace(/_/g, ' ')}
                                    {condition.threshold_value != null ? ` ${condition.threshold_value}` : ''}
                                  </span>
                                ))}
                              </div>
                            </Col>
                            <Col md={12}>
                          <div className="auto-trade-muted">
                            Last triggered: {formatToLocalTime(rule.last_triggered_at)}
                          </div>
                          <div className="auto-trade-muted">
                            Last evaluated: {formatToLocalTime(rule.last_evaluated_at)}
                          </div>
                          {rule.last_execution_details && (
                            <div className="auto-trade-muted">
                              Details: {rule.last_execution_details}
                            </div>
                          )}
                          {(() => {
                            const directTargetSymbols = (rule.targets || [])
                              .filter((target) => target?.target_type === 'SYMBOL' && (target?.ticker_symbol || '').trim())
                              .map((target) => String(target.ticker_symbol).toUpperCase());
                            const watchlistTargetSymbols = (rule.targets || [])
                              .filter((target) => target?.target_type === 'WATCHLIST' && target?.watchlist_id != null)
                              .flatMap((target) => {
                                const watchlistId = String(target.watchlist_id);
                                const symbolsForWatchlist = watchlistSymbolMap?.[watchlistId]
                                  || watchlistSymbolMap?.[Number(watchlistId)]
                                  || [];
                                return Array.isArray(symbolsForWatchlist)
                                  ? symbolsForWatchlist.map((symbol) => String(symbol).toUpperCase())
                                  : [];
                              });
                            const detailRows = Array.isArray(rule.symbol_details) ? rule.symbol_details : [];
                            const detailsBySymbol = {};
                            detailRows.forEach((row) => {
                              const symbol = String(row?.symbol || '').toUpperCase();
                              if (symbol) detailsBySymbol[symbol] = row;
                            });
                            // Backward compatibility: older API shape stores per-symbol state in target.symbol_details JSON.
                            (rule.targets || []).forEach((target) => {
                              const map = parseSymbolDetailsMap(target?.symbol_details);
                              Object.entries(map).forEach(([symbolKey, state]) => {
                                const symbol = String(symbolKey || '').toUpperCase();
                                if (!symbol) return;
                                if (detailsBySymbol[symbol]) return;
                                detailsBySymbol[symbol] = {
                                  symbol,
                                  last_evaluated_at: state?.last_evaluated_at ?? null,
                                  last_triggered_at: state?.last_triggered_at ?? null,
                                  last_condition_met: state?.last_condition_met ?? null,
                                  details: state?.details ?? null,
                                  bypass_cooldown_once: !!state?.bypass_cooldown_once,
                                };
                              });
                            });
                            const mergedSymbols = Array.from(new Set([
                              ...directTargetSymbols,
                              ...watchlistTargetSymbols,
                              ...Object.keys(detailsBySymbol),
                            ])).sort();
                            if (mergedSymbols.length === 0) return null;

                            const rows = mergedSymbols.map((symbol) => ({
                              symbol,
                              ...(detailsBySymbol[symbol] || {}),
                            }));

                            return (
                              <div className="mt-3">
                                <div className="auto-trade-section-label">Symbol Comparison</div>
                                <div className="table-responsive">
                                  <table className="table table-sm">
                                    <thead>
                                      <tr>
                                        <th>Symbol</th>
                                        <th>Last Evaluated</th>
                                        <th>Last Triggered</th>
                                        <th>Condition</th>
                                        <th>Details</th>
                                        <th>Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {rows.map((row, idx) => (
                                        <tr key={`${row.symbol}-${idx}`}>
                                          <td>{row.symbol}</td>
                                          <td>{formatToLocalTime(row.last_evaluated_at)}</td>
                                          <td>{formatToLocalTime(row.last_triggered_at)}</td>
                                          <td>
                                            {row.last_condition_met == null
                                              ? '—'
                                              : (row.last_condition_met ? 'Met' : 'Not met')}
                                          </td>
                                          <td>{row.details || '—'}</td>
                                          <td>
                                            <Button
                                              variant={isSymbolPaused(row.symbol) ? 'outline-success' : 'outline-warning'}
                                              size="sm"
                                              className="auto-trade-bypass-btn me-2"
                                              onClick={() =>
                                                updatePauseState(
                                                  isSymbolPaused(row.symbol) ? 'resume' : 'pause',
                                                  'SYMBOL',
                                                  { symbol: row.symbol }
                                                )
                                              }
                                            >
                                              {isSymbolPaused(row.symbol) ? 'Resume Stock' : 'Pause Stock'}
                                            </Button>
                                            <Button
                                              variant={row.bypass_cooldown_once ? 'outline-secondary' : 'outline-warning'}
                                              size="sm"
                                              className="auto-trade-bypass-btn"
                                              disabled={!!row.bypass_cooldown_once || isSymbolPaused(row.symbol)}
                                              onClick={() => bypassCooldown(rule.id, row.symbol)}
                                            >
                                              {row.bypass_cooldown_once ? 'Bypass Armed' : 'Bypass Cooldown'}
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          })()}
                        </Col>
                      </Row>
                            </div>
                          )}
                    </div>
                  ))}
                    </div>
                  )}
                </Tab>
                <Tab eventKey="executions" title="Executions">
                  <div className="auto-trade-section-label">Execution Comparison</div>
                  {executionComparison.rows.length === 0 ? (
                    <div className="auto-trade-empty">No execution data to chart yet.</div>
                  ) : (
                    <div className="auto-trade-execution-chart-wrap mb-3">
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={executionComparison.rows} margin={{ top: 12, right: 20, left: 6, bottom: 28 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.12)" />
                          <XAxis
                            dataKey="date"
                            interval="preserveStartEnd"
                            tick={{ fontSize: 11 }}
                          />
                          <YAxis allowDecimals={false} />
                          <Tooltip
                            formatter={(value, name) => [
                              value,
                              name === 'buy_count'
                                ? 'BUY Count'
                                : name === 'sell_count'
                                  ? 'SELL Count'
                                  : String(name).replace(/^detail_/, '').replace(/_/g, ' '),
                            ]}
                            labelFormatter={(label) => `Date: ${label}`}
                          />
                          <Legend />
                          <Bar dataKey="buy_count" name="BUY" fill="#2563eb" radius={[3, 3, 0, 0]} />
                          <Bar dataKey="sell_count" name="SELL" fill="#0f766e" radius={[3, 3, 0, 0]} />
                          {executionComparison.detailKeys.map((item, idx) => {
                            const detailPalette = ['#7c3aed', '#f59e0b', '#ef4444', '#14b8a6', '#8b5cf6', '#334155'];
                            return (
                              <Bar
                                key={`detail-series-${item.key}`}
                                dataKey={item.key}
                                name={item.label}
                                fill={detailPalette[idx % detailPalette.length]}
                                radius={[3, 3, 0, 0]}
                              />
                            );
                          })}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="auto-trade-section-label mb-0">Execution History</span>
                    <Button variant="outline-secondary" size="sm" onClick={fetchExecutions}>
                      Refresh
                    </Button>
                  </div>
                  {loadingExec ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : unifiedExecutionRows.length === 0 ? (
                    <div className="auto-trade-empty">No auto-trade records yet.</div>
                  ) : (
                    <div className="auto-trade-table">
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Symbol</th>
                            <th>Buy Time</th>
                            <th className="text-end">Entry Price</th>
                            <th className="text-end">Buy Qty</th>
                            <th>Sell Time</th>
                            <th className="text-end">Exit Price</th>
                            <th className="text-end">Sell Qty</th>
                            <th className="text-end">P&amp;L</th>
                            <th className="text-end">P&amp;L %</th>
                            <th>Status</th>
                            <th>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {executionsGroupedByStatus.map((group) => (
                            <React.Fragment key={`exec-group-${group.status}`}>
                              <tr className="table-light">
                                <td colSpan={11}>
                                  <strong>{group.status}</strong> ({group.rows.length})
                                </td>
                              </tr>
                              {group.rows.map(exec => (
                                <tr key={`unified-exec-${group.status}-${exec.id}`}>
                                  <td>{exec.ticker_symbol}</td>
                                  <td>{formatToLocalTime(exec.buy_time)}</td>
                                  <td className="text-end">{exec.entry_price != null ? `$${Number(exec.entry_price).toFixed(2)}` : '—'}</td>
                                  <td className="text-end">{exec.buy_quantity}</td>
                                  <td>{exec.sell_time ? formatToLocalTime(exec.sell_time) : '—'}</td>
                                  <td className="text-end">{exec.exit_price != null ? `$${Number(exec.exit_price).toFixed(2)}` : '—'}</td>
                                  <td className="text-end">{exec.sell_quantity != null ? exec.sell_quantity : '—'}</td>
                                  <td className={`text-end auto-trade-perf-text-${exec.derived_status === 'CLOSED' ? toneFromValue(exec.pnl) : 'neutral'}`}>
                                    {exec.pnl != null ? formatCurrency(exec.pnl) : '—'}
                                  </td>
                                  <td className={`text-end auto-trade-perf-text-${exec.derived_status === 'CLOSED' ? toneFromValue(exec.pnl_pct) : 'neutral'}`}>
                                    {exec.pnl_pct != null ? formatPercent(exec.pnl_pct, 2) : '—'}
                                  </td>
                                  <td>{exec.derived_status}</td>
                                  <td>{exec.details || '—'}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Tab>
                <Tab eventKey="performance" title="Performance">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="auto-trade-section-label mb-0">Performance</span>
                    <div className="d-flex gap-2 align-items-center">
                      <Form.Select
                        size="sm"
                        value={perfRange}
                        onChange={(e) => {
                          setPerfRange(e.target.value);
                          fetchPerformance(e.target.value);
                          fetchAutoTrades();
                        }}
                      >
                        <option value="1">1 day</option>
                        <option value="3">3 day</option>
                        <option value="7">7 day</option>
                        <option value="15">15 day</option>
                        <option value="30">30 day</option>
                        <option value="90">90 day</option>
                        <option value="all">All</option>
                      </Form.Select>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          fetchPerformance(perfRange);
                          fetchAutoTrades();
                        }}
                      >
                        Refresh
                      </Button>
                    </div>
                  </div>
                  {loadingPerf ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : !hasPerfData ? (
                    <div className="auto-trade-empty">No performance data yet.</div>
                  ) : (
                    <>
                      <Row className="g-3 mb-3">
                        <Col md={3}>
                          <div className={`auto-trade-perf-card auto-trade-perf-${toneFromValue(perfData.overall?.total_pnl)}`}>
                            <div className="auto-trade-perf-label">Total P&L</div>
                            <div className="auto-trade-perf-value">
                              {formatCurrency(perfData.overall?.total_pnl)}
                            </div>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="auto-trade-perf-card auto-trade-perf-neutral">
                            <div className="auto-trade-perf-label">Win Rate</div>
                            <div className="auto-trade-perf-value">
                              {formatPercent(perfData.overall?.win_rate, 1)}
                            </div>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className={`auto-trade-perf-card auto-trade-perf-${toneFromValue(perfData.overall?.avg_return_pct)}`}>
                            <div className="auto-trade-perf-label">Avg Return</div>
                            <div className="auto-trade-perf-value">
                              {formatPercent(perfData.overall?.avg_return_pct, 2)}
                            </div>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="auto-trade-perf-card auto-trade-perf-neutral">
                            <div className="auto-trade-perf-label">Trades</div>
                            <div className="auto-trade-perf-value">
                              {perfData.overall?.trades ?? 0}
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <div className="auto-trade-perf-trend">
                        <div className="auto-trade-perf-trend-header">
                          <div>
                            <div className="auto-trade-perf-label">P&L Trend</div>
                            <div className="auto-trade-perf-subtitle">
                              Based on realized auto trades in the selected window
                            </div>
                          </div>
                          <span className={`auto-trade-metric-chip auto-trade-chip-${toneFromValue(perfTrend.latest)}`}>
                            {formatCurrency(perfTrend.latest)}
                          </span>
                        </div>
                        {perfTrend.points.length === 0 ? (
                          <div className="auto-trade-empty">No realized trades to chart.</div>
                        ) : (
                          <svg className="auto-trade-sparkline" viewBox="0 0 320 80" preserveAspectRatio="none">
                            {(() => {
                              const width = 320;
                              const height = 80;
                              const padding = 6;
                              const usableWidth = width - padding * 2;
                              const usableHeight = height - padding * 2;
                              const count = perfTrend.points.length;
                              const min = Math.min(0, perfTrend.min ?? 0);
                              const max = Math.max(0, perfTrend.max ?? 0);
                              const span = max - min || 1;
                              const pointX = (idx) => (count <= 1 ? padding + usableWidth / 2 : padding + (usableWidth * idx) / (count - 1));
                              const pointY = (value) => padding + usableHeight - ((value - min) / span) * usableHeight;
                              const zeroY = pointY(0);
                              const barSlotWidth = usableWidth / Math.max(count, 1);
                              const barWidth = Math.max(3, Math.min(10, barSlotWidth * 0.56));
                              const linePath = perfTrend.points
                                .map((value, idx) => `${idx === 0 ? 'M' : 'L'} ${pointX(idx)} ${pointY(value)}`)
                                .join(' ');
                              const areaPath = `${linePath} L ${pointX(count - 1)} ${zeroY} L ${pointX(0)} ${zeroY} Z`;
                              const positiveClipHeight = Math.max(Math.min(zeroY, height), 0);
                              const negativeClipY = Math.max(Math.min(zeroY, height), 0);
                              const negativeClipHeight = Math.max(height - negativeClipY, 0);
                              const strokeColor = (perfTrend.latest ?? 0) >= 0 ? '#16a34a' : '#dc2626';
                              return (
                                <>
                                  <defs>
                                    <clipPath id="auto-trade-main-trend-pos-clip">
                                      <rect x="0" y="0" width={width} height={positiveClipHeight} />
                                    </clipPath>
                                    <clipPath id="auto-trade-main-trend-neg-clip">
                                      <rect x="0" y={negativeClipY} width={width} height={negativeClipHeight} />
                                    </clipPath>
                                  </defs>
                                  <line
                                    x1={padding}
                                    y1={zeroY}
                                    x2={width - padding}
                                    y2={zeroY}
                                    stroke="rgba(15, 23, 42, 0.2)"
                                    strokeWidth="1"
                                  />
                                  {(perfTrend.deltas || []).map((deltaValue, idx) => {
                                    const xCenter = pointX(idx);
                                    const deltaY = pointY(deltaValue);
                                    const y = deltaValue >= 0 ? deltaY : zeroY;
                                    const heightBar = Math.max(Math.abs(deltaY - zeroY), 1.2);
                                    const fill = deltaValue >= 0 ? 'rgba(22, 163, 74, 0.66)' : 'rgba(220, 38, 38, 0.66)';
                                    return (
                                      <rect
                                        key={`perf-delta-bar-${idx}`}
                                        x={xCenter - barWidth / 2}
                                        y={y}
                                        width={barWidth}
                                        height={heightBar}
                                        rx="1.5"
                                        fill={fill}
                                      />
                                    );
                                  })}
                                  <path
                                    d={areaPath}
                                    fill="rgba(22, 163, 74, 0.2)"
                                    clipPath="url(#auto-trade-main-trend-pos-clip)"
                                    stroke="none"
                                  />
                                  <path
                                    d={areaPath}
                                    fill="rgba(220, 38, 38, 0.2)"
                                    clipPath="url(#auto-trade-main-trend-neg-clip)"
                                    stroke="none"
                                  />
                                  <path
                                    d={linePath}
                                    fill="none"
                                    stroke={strokeColor}
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </>
                              );
                            })()}
                          </svg>
                        )}
                      </div>
                      <div className="auto-trade-section-label">By Rule</div>
                      {(perfData.by_rule || []).length === 0 ? (
                        <div className="auto-trade-empty">No rule-level performance yet.</div>
                      ) : (
                        (perfData.by_rule || []).map(row => (
                          <div key={`rule-${row.rule_set_id}`} className="auto-trade-exec-row auto-trade-perf-row">
                            <div className="auto-trade-exec-title">{row.rule_name || `Rule #${row.rule_set_id}`}</div>
                            <div className="auto-trade-perf-metrics">
                              <span className="auto-trade-metric-chip">Trades {row.trades ?? 0}</span>
                              <span className={`auto-trade-metric-chip auto-trade-chip-win-${toneFromWinRate(row.win_rate)}`}>Win {formatPercent(row.win_rate, 1)}</span>
                              <span className={`auto-trade-metric-chip auto-trade-chip-${toneFromValue(row.total_pnl)}`}>
                                P&L {formatCurrency(row.total_pnl)}
                              </span>
                              <span className={`auto-trade-metric-chip auto-trade-chip-${toneFromValue(row.avg_return_pct)}`}>
                                Avg {formatPercent(row.avg_return_pct, 2)}
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                      <div className="auto-trade-section-label mt-3">By Rule + Stock</div>
                      {byRuleSymbolGroups.length === 0 ? (
                        <div className="auto-trade-empty">No rule+stock performance yet.</div>
                      ) : (
                        byRuleSymbolGroups.map((group) => (
                          <div key={`rule-stock-group-${group.rule_set_id}`} className="auto-trade-rule-stock-group">
                            <div className="auto-trade-rule-stock-group-header">
                              {group.rule_name}
                              <span className="auto-trade-rule-stock-group-count">({group.rows.length})</span>
                            </div>
                            {group.rows.map((row) => (
                              <div key={`rule-stock-${row.rule_set_id}-${row.ticker_symbol}`} className="auto-trade-exec-row auto-trade-perf-row">
                                <div className="auto-trade-exec-title">
                                  {row.ticker_symbol}
                                </div>
                                <div className="auto-trade-perf-mini-trend">
                                  {renderMiniSparkline(
                                    perfComboTrends.byRuleSymbol[`${row.rule_set_id}::${row.ticker_symbol}`],
                                    {
                                      label: `${row.ticker_symbol}`,
                                      pnl: row.total_pnl,
                                      pnlPct: row.avg_return_pct,
                                      tooltipKey: `rule-symbol-${row.rule_set_id}-${row.ticker_symbol}`,
                                    }
                                  )}
                                </div>
                                <div className="auto-trade-perf-metrics">
                                  <span className="auto-trade-metric-chip">Trades {row.trades ?? 0}</span>
                                  <span className={`auto-trade-metric-chip auto-trade-chip-win-${toneFromWinRate(row.win_rate)}`}>Win {formatPercent(row.win_rate, 1)}</span>
                                  <span className={`auto-trade-metric-chip auto-trade-chip-${toneFromValue(row.total_pnl)}`}>
                                    P&L {formatCurrency(row.total_pnl)}
                                  </span>
                                  <span className={`auto-trade-metric-chip auto-trade-chip-${toneFromValue(row.avg_return_pct)}`}>
                                    Avg {formatPercent(row.avg_return_pct, 2)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ))
                      )}
                      <div className="auto-trade-section-label mt-3">By Rule + Watchlist</div>
                      {byRuleWatchlistGroups.length === 0 ? (
                        <div className="auto-trade-empty">No rule+watchlist performance yet.</div>
                      ) : (
                        byRuleWatchlistGroups.map((group) => (
                          <div key={`rule-watchlist-group-${group.rule_set_id}`} className="auto-trade-rule-stock-group">
                            <div className="auto-trade-rule-stock-group-header">
                              {group.rule_name}
                              <span className="auto-trade-rule-stock-group-count">({group.rows.length})</span>
                            </div>
                            {group.rows.map((row) => (
                              <div key={`rule-watchlist-${row.rule_set_id}-${row.watchlist_id}`} className="auto-trade-exec-row auto-trade-perf-row">
                                <div className="auto-trade-exec-title">
                                  {row.watchlist_name || watchlistNameMap[row.watchlist_id] || `Watchlist #${row.watchlist_id}`}
                                </div>
                                <div className="auto-trade-perf-mini-trend">
                                  {renderMiniSparkline(
                                    perfComboTrends.byRuleWatchlist[`${row.rule_set_id}::${row.watchlist_id}`],
                                    {
                                      label: `${row.watchlist_name || watchlistNameMap[row.watchlist_id] || `Watchlist #${row.watchlist_id}`}`,
                                      pnl: row.total_pnl,
                                      pnlPct: row.avg_return_pct,
                                      tooltipKey: `rule-watchlist-${row.rule_set_id}-${row.watchlist_id}`,
                                    }
                                  )}
                                </div>
                                <div className="auto-trade-perf-metrics">
                                  <span className="auto-trade-metric-chip">Trades {row.trades ?? 0}</span>
                                  <span className={`auto-trade-metric-chip auto-trade-chip-win-${toneFromWinRate(row.win_rate)}`}>Win {formatPercent(row.win_rate, 1)}</span>
                                  <span className={`auto-trade-metric-chip auto-trade-chip-${toneFromValue(row.total_pnl)}`}>
                                    P&L {formatCurrency(row.total_pnl)}
                                  </span>
                                  <span className={`auto-trade-metric-chip auto-trade-chip-${toneFromValue(row.avg_return_pct)}`}>
                                    Avg {formatPercent(row.avg_return_pct, 2)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ))
                      )}
                    </>
                  )}
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Auto-Trade Rule</Modal.Title>
        </Modal.Header>
        <Modal.Body>{RuleForm}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdate} disabled={saving}>
            {saving ? 'Saving...' : 'Update Rule'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AutoTradingRules;
